import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Empty, Notice, Panel } from '../design/index.js'
import { useApi } from '../app/api.js'
import { Link } from '../app/Link.js'
import { paths } from '../app/router.js'
import { ErrorBlock, Loading } from './feedback.js'

/**
 * Is this job ready to buy for? (docs/24 §3.1)
 *
 * The notes' "project awarded" step as a checklist: the document pack, the
 * time line, the approved estimate, every material classed and with a lead
 * time, a data sheet for every material that matters, and nothing already
 * late to order. Each line says what was found, and what is missing where
 * there is a list to give — the missing list is the to-do.
 */
export const KickoffPanel = ({ projectId }: { projectId: string }) => {
  const api = useApi()
  const client = useQueryClient()
  const key = ['kickoff', api.companyId, projectId]
  const readiness = useQuery({
    queryKey: key, queryFn: () => api.kickoffReadiness(projectId), enabled: projectId !== '',
  })
  const setUp = useMutation({
    mutationFn: () => api.ensureKickoffCategories(),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: key })
      // The Files tab's category filter and the details form read the same master.
      void client.invalidateQueries({ queryKey: ['master-list', api.companyId, 'file_category'] })
    },
  })

  if (projectId === '') {
    return <Empty title="No project chosen" body={['Pick a project above to see whether it is ready to buy for.']} />
  }

  if (readiness.isLoading) return <Loading />
  if (readiness.error) return <ErrorBlock error={readiness.error} />
  const data = readiness.data
  if (!data) return <Empty title="Nothing to show" body={['The readiness check could not be read.']} />

  const passed = data.checks.filter((c) => c.ok).length
  return (
    <Panel title="Ready to buy for?" aside={`${passed} of ${data.checks.length} · ${data.score}%`}>
      {!data.categoriesReady ? (
        <Notice kind="warn">
          This company has not set up the kick-off file categories — drawings, approved drawings,
          terms, procedures, data sheets.{' '}
          <Button kind="ghost" disabled={setUp.isPending} onClick={() => setUp.mutate()}>Set them up</Button>
        </Notice>
      ) : null}
      {setUp.error ? <ErrorBlock error={setUp.error} /> : null}
      <ul className="checklist" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {data.checks.map((check) => (
          <li key={check.key} style={{ padding: '10px 0', borderBottom: '1px solid var(--line)' }}>
            <div className="row" style={{ gap: 10, alignItems: 'baseline' }}>
              <span aria-hidden="true" style={{ fontWeight: 700, color: check.ok ? 'var(--good)' : 'var(--bad)' }}>
                {check.ok ? '✓' : '✗'}
              </span>
              <strong>{check.label}</strong>
              <span className="helper">{check.detail}</span>
            </div>
            {check.missing.length > 0 ? (
              <div className="helper" style={{ marginLeft: 22, marginTop: 4 }}>
                {check.missing.join(' · ')}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      <p className="helper" style={{ marginTop: 14 }}>
        Classes and lead times are set on each material, or its category. See the{' '}
        <Link to={paths.report(api.companyId, 'abc_analysis')}>ABC analysis</Link> and the{' '}
        <Link to={paths.report(api.companyId, 'mrp')}>material requirement plan</Link>.
      </p>
    </Panel>
  )
}
